import { FormControl } from 'baseui/form-control';
import { FastField, Field, FieldProps, FieldValidator } from 'formik';
import { FakeFormikEvent } from '../types';
import { DatepickerProps } from 'baseui/datepicker';
import { customShouldComponentUpdate } from './fastFormFieldFunctions';
import DatePickerExtra from '../inputs/DatePickerExtra';
import { isDateValid } from '@@shared/functions/general';
import { useTranslation } from 'react-i18next';
import { MutableRefObject, useRef } from 'react';

type SafeDatePickerProps = Omit<DatepickerProps, 'value' | 'onChange'>;

interface CommonDateFieldProps {
  label: string;
  noFuture?: boolean;
}

interface DateFormControlProps extends CommonDateFieldProps, SafeDatePickerProps {
  fieldProps: FieldProps;
  rawInputRef: MutableRefObject<string>;
}

export interface DateFieldProps extends CommonDateFieldProps, SafeDatePickerProps {
  name: string;
  validate?: FieldValidator;
}

function useDateFormatValidator(callerValidate?: FieldValidator) {
  const tShared = useTranslation('translation', { keyPrefix: 'shared' }).t;
  const rawInputRef = useRef('');

  const validate: FieldValidator = (fieldValue) => {
    const callerError = callerValidate?.(fieldValue);
    if (callerError) return callerError;

    const value = rawInputRef.current;
    if (!value || isDateValid(value)) return;

    return tShared('validDate') as string;
  };

  return { rawInputRef, validate };
}

function DateFormControl(props: DateFormControlProps) {
  const { fieldProps, label, disabled, noFuture, rawInputRef, ...restOfProps } = props;

  const { field, meta, form } = fieldProps;

  const showError = meta.touched && !!meta.error;

  return (
    <FormControl label={label} disabled={disabled} error={showError && meta.error}>
      <div
        onBlur={(e) => {
          rawInputRef.current = (e.target as HTMLInputElement).value ?? '';
          form.setFieldTouched(field.name, true, true);
        }}
      >
        <DatePickerExtra
          value={field.value}
          onChange={({ date }) => {
            const newValue = Array.isArray(date) ? date[0] : date;
            rawInputRef.current = '';
            field.onChange(new FakeFormikEvent(field.name, newValue));
          }}
          useZonedTime
          maxDate={noFuture ? new Date() : undefined}
          formatString="dd.MM.yyyy"
          placeholder="dd.mm.yyyy"
          disabled={disabled}
          {...restOfProps}
        />
      </div>
    </FormControl>
  );
}

export function FastDateField(props: DateFieldProps) {
  const { name, validate, ...restOfProps } = props;

  const { rawInputRef, validate: dateValidator } = useDateFormatValidator(validate);

  const depProps = { disabled: props.disabled, required: props.required };

  return (
    <FastField name={name} validate={dateValidator} shouldUpdate={customShouldComponentUpdate} {...depProps}>
      {(fieldProps: FieldProps) => (
        <DateFormControl {...restOfProps} fieldProps={fieldProps} rawInputRef={rawInputRef} />
      )}
    </FastField>
  );
}

export default function DateField(props: DateFieldProps) {
  const { name, validate, ...restOfProps } = props;

  const { rawInputRef, validate: dateValidator } = useDateFormatValidator(validate);

  return (
    <Field name={name} validate={dateValidator}>
      {(fieldProps: FieldProps) => (
        <DateFormControl {...restOfProps} fieldProps={fieldProps} rawInputRef={rawInputRef} />
      )}
    </Field>
  );
}
